import { useSyncExternalStore } from 'react';
import { Monitor, Moon, Sun } from 'lucide-react';
import type { ResolvedTheme } from '../theme/ThemeRoot';
import { useConsoleI18n } from './bind';

/** What the user picked; `system` follows `prefers-color-scheme`. Dark is the default (see `configureTheme`). */
export type ThemeChoice = 'dark' | 'light' | 'system';

let storageKey = 'nyce:theme';
let defaultChoice: ThemeChoice = 'dark';

/**
 * Where the choice is kept (localStorage, default `nyce:theme`) and the product's theme while nothing is stored
 * (default dark); call before `applyTheme` (main.tsx).
 */
export function configureTheme(options: { storageKey: string; defaultChoice?: ThemeChoice }): void {
  storageKey = options.storageKey;
  defaultChoice = options.defaultChoice ?? 'dark';
  snapshot = null;
}
const DARK_QUERY = '(prefers-color-scheme: dark)';
const CHOICES: ThemeChoice[] = ['dark', 'light', 'system'];

export function readThemeChoice(): ThemeChoice {
  try {
    const v = localStorage.getItem(storageKey);
    if (v && (CHOICES as string[]).includes(v)) return v as ThemeChoice;
  } catch {
    /* storage blocked */
  }
  return defaultChoice;
}

const systemPrefersDark = () =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(DARK_QUERY).matches
    : true;

export const resolveTheme = (choice: ThemeChoice): ResolvedTheme =>
  choice === 'system' ? (systemPrefersDark() ? 'dark' : 'light') : choice;

/** Put the resolved theme on `<html data-theme>` (the `--nyk-*` tokens hang off it) and set `color-scheme`. */
export function applyTheme(choice: ThemeChoice = readThemeChoice()): ResolvedTheme {
  const resolved = resolveTheme(choice);
  if (typeof document !== 'undefined') {
    document.documentElement.dataset.theme = resolved;
    document.documentElement.style.colorScheme = resolved;
  }
  return resolved;
}

const listeners = new Set<() => void>();
let snapshot: { choice: ThemeChoice; resolved: ResolvedTheme } | null = null;
const current = () => {
  const choice = readThemeChoice();
  const resolved = resolveTheme(choice);
  if (!snapshot || snapshot.choice !== choice || snapshot.resolved !== resolved)
    snapshot = { choice, resolved };
  return snapshot;
};
const notify = () => {
  applyTheme();
  listeners.forEach((l) => l());
};

export function setThemeChoice(choice: ThemeChoice): void {
  try {
    localStorage.setItem(storageKey, choice);
  } catch {
    /* storage blocked: the choice lasts for this page only */
  }
  notify();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  const mql =
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(DARK_QUERY)
      : null;
  mql?.addEventListener?.('change', notify);
  return () => {
    listeners.delete(listener);
    mql?.removeEventListener?.('change', notify);
  };
}

/** The current choice and its resolved theme; follows toggles and, for `system`, the OS setting. */
export function useTheme() {
  return useSyncExternalStore(subscribe, current, current);
}

const NEXT: Record<ThemeChoice, ThemeChoice> = { dark: 'light', light: 'system', system: 'dark' };
const ICON = { dark: Moon, light: Sun, system: Monitor };

/** Cycles dark → light → system; the icon shows the current choice. */
export function ThemeToggle({ className }: { className?: string }) {
  const { t } = useConsoleI18n();
  const { choice } = useTheme();
  const Icon = ICON[choice];
  const label = t('shell.themeLabel', {
    current: t(`shell.theme.${choice}`),
    next: t(`shell.theme.${NEXT[choice]}`),
  });
  return (
    <button
      type="button"
      className={['nyk-theme-btn', className].filter(Boolean).join(' ')}
      aria-label={label}
      title={label}
      data-theme-choice={choice}
      onClick={() => setThemeChoice(NEXT[choice])}
    >
      <Icon size={18} aria-hidden="true" />
    </button>
  );
}
